import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { getNonce, getTables, isPro } from './../Helpers';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';
import jsonIcon from '../../../assets/public/icons/export/Json.svg';
import csvIcon from '../../../assets/public/icons/export/csv.svg';
import pdfIcon from '../../../assets/public/icons/export/pdf.svg';
import printIcon from '../../../assets/public/icons/export/print.svg';
import deleteConfirmImage from '../../../assets/public/icons/delete-icon.gif';
import NoticeModal from '../components/NoticeModal/NoticeModal';
import jsonexport from 'jsonexport';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import '../styles/_lead.scss';

const Leads = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedLeadData, setSelectedLeadData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteID] = useState(null);
  const [loader, setLoader] = useState(true);
  const [totalTables, setTotalTables] = useState(0);
  const confirmDeleteRef = useRef(null);
  const [selectedLeads, setSelectedLeads] = useState([]);

  // Date filtering states
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'confirmation',
    title: '',
    message: '',
    onConfirm: () => { },
    confirmText: 'Confirm',
    declineText: 'Cancel'
  });

  /* const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  }; */

  // Export leads to different formats
  const exportLeads = async (format) => {
    if (leads.length === 0) {
      setModalConfig({
        isOpen: true,
        type: 'toast',
        title: 'No Data!',
        message: 'There are no leads to export',
        position: 'top-right'
      });
      return;
    }

    const processLeadData = () => leads.map((lead) => {
      const fields = JSON.parse(lead.fields);
      return Object.keys(fields).reduce((acc, key) => {
        acc[key] = fields[key];
        return acc;
      }, {});
    });

    switch (format) {
      case 'json':
        const jsonData = processLeadData();
        saveAs(new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' }), 'leads.json');
        showExportSuccess('JSON');
        break;

      case 'csv':
        const csvData = processLeadData();
        jsonexport(csvData, (err, csv) => {
          if (err) {
            console.error(err);
            return;
          }
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
          saveAs(blob, 'leads.csv');
          showExportSuccess('CSV');
        });
        break;

      case 'print':
        printLeads();
        break;

      case 'pdf':
        const pdfData = processLeadData();
        if (pdfData.length > 0) {
          const pdf = new jsPDF();
          pdf.autoTable({
            head: [Object.keys(pdfData[0])],
            body: pdfData.map((data) => Object.values(data)),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [82, 113, 255] },
          });
          pdf.save('leads.pdf');
          showExportSuccess('PDF');
        }
        break;
    }
  };

  // Add this function near your other export functions at the top
  const exportSingleLead = (leadData) => {
    if (!leadData) return;

    // Prepare data for export - just the fields from the lead
    const fields = JSON.parse(leadData.fields);

    // Add metadata if available
    const metadata = leadData.meta ? JSON.parse(leadData.meta) : {};
    const exportData = [{ ...fields, ...metadata }];

    jsonexport(exportData, (err, csv) => {
      if (err) {
        console.error(err);
        setModalConfig({
          isOpen: true,
          type: 'toast',
          title: 'Export Failed!',
          message: 'There was a problem exporting the lead',
          position: 'top-right'
        });
        return;
      }

      // Create and download CSV file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `lead-${leadData.id}.csv`);

      // Show success message
      setModalConfig({
        isOpen: true,
        type: 'toast',
        title: 'Success!',
        message: 'Lead exported as CSV successfully',
        position: 'top-right'
      });

      // Auto close the toast after 3 seconds
      setTimeout(closeModal, 3000);
    });
  };

  const showExportSuccess = (format) => {
    setModalConfig({
      isOpen: true,
      type: 'toast',
      title: 'success!',
      message: `Leads exported as ${format} successfully`,
      position: 'top-right'
    });
    setTimeout(closeModal, 3000);

  };

  // Print leads
  const printLeads = () => {
    const printWindow = window.open('', '_blank');
    const selectedFormName = tables.find(table => table.id === selectedId)?.form_name || 'Leads';

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Leads - ${selectedFormName}</title>
          <style>
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              padding: 20px;
              color: #333;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 1px solid #eee;
            }
            h2 {
              color: #5271FF;
              margin: 0;
            }
            .date {
              color: #666;
              font-size: 14px;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              margin-top: 20px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.05);
            }
            th, td {
              border: 1px solid #eee;
              text-align: left;
              padding: 12px;
            }
            th {
              background-color: #5271FF;
              color: white;
              font-weight: 500;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .footer {
              margin-top: 30px;
              font-size: 12px;
              text-align: center;
              color: #888;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>${selectedFormName} - Leads Report</h2>
            <div class="date">${new Date().toLocaleDateString()}</div>
          </div>
          
          <table>
            <thead>
              <tr>
                ${columns.filter(column => column.field !== 'actions').map(column =>
      `<th>${column.headerName}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${rows.map(lead => `
                <tr>
                  ${columns.filter(column => column.field !== 'actions').map(column => {
        const cellValue = lead[column.field];
        const isImageColumn = typeof cellValue === 'string' && cellValue.startsWith('data:image');

        return `<td>${cellValue !== undefined ?
          (isImageColumn ?
            `<img src="${cellValue}" alt="Lead Image" style="max-width: 100px; max-height: 100px;" />` :
            cellValue) :
          ''}</td>`;
      }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            Generated on ${new Date().toLocaleString()} | Total Leads: ${leads.length}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.onload = function () {
      printWindow.print();
      printWindow.close();
    };
  };

  // Custom render for image cells
  const ImageCellRenderer = (params) => {
    if (!params.value || typeof params.value !== 'string') {
      return <span className="no-image">No Image</span>;
    }

    const isImageData = params.value.startsWith('data:image') || params.value.startsWith('http');

    if (isImageData) {
      return (
        <div className="image-cell">
          <img
            src={params.value}
            alt="Lead Image"
            className="lead-image"
            onError={(e) => {
              e.currentTarget.src = 'fallback-image.svg';
              e.currentTarget.classList.add('error-image');
            }}
          />
        </div>
      );
    }

    return <span>{params.value}</span>;
  };

  // Modal for displaying full lead details
  const openModal = (leadId, event) => {
    event.stopPropagation();
    const selectedLead = leads.find((lead) => lead.id === leadId);
    if (selectedLead) {
      setSelectedLeadData(selectedLead);
      setModalVisible(true);
    }
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
    setModalVisible(false);
  };

  // Delete lead functionality
  const handleDeleteTable = (id, event) => {
    event.stopPropagation();
    setDeleteID(id);
    setDeleteModal(true);
  };

  const handleClosePopup = () => {
    setDeleteModal(false);
  };

  const deleteLead = (id) => {
    wp.ajax.send('simpleform_delete_leads', {
      data: {
        nonce: getNonce(),
        id,
      },
      success() {
        setDeleteModal(false);
        const updatedLeads = leads.filter((lead) => lead.id !== id);
        setLeads(updatedLeads);
        setModalConfig({
          isOpen: true,
          type: 'toast',
          title: 'success!',
          message: 'Lead deleted successfully',
          position: 'top-right'
        });

        setTimeout(closeModal, 3000);

      },
      error(error) {
        console.error(error);
        setModalConfig({
          isOpen: true,
          type: 'toast',
          title: 'Delete Failed!',
          message: 'There was a problem deleting the lead',
          position: 'top-right'
        });
        setTimeout(closeModal, 3000);

      },
    });
  };

  // Bulk delete selected leads
  const bulkDeleteLeads = () => {
    if (selectedLeads.length === 0) {
      setModalConfig({
        isOpen: true,
        type: 'toast',
        title: 'No Selection!',
        message: `Please select leads to delete`,
        position: 'top-right'
      });
      return;
    }


    setModalConfig({
      isOpen: true,
      type: 'confirmation',
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete ${selectedLeads.length} selected leads?`,
      onConfirm: () => {

        setSelectedLeads([]);

        setModalConfig({
          isOpen: true,
          type: 'toast',
          title: 'Reset Failed!',
          message: 'Reset Failed.',
          position: 'top-right'
        });

        // Auto close the toast after 3 seconds
        setTimeout(closeModal, 3000);

        // Close the confirmation modal immediately
        closeModal();
      },
      confirmText: 'Yes, Reset!',
      declineText: 'Cancel'
    });

  };

  // Form selection handler
  const handleSelectChange = (e) => {
    const formId = e.target.value;
    if (formId) {
      setSelectedId(formId);

      // Optional: Update URL to reflect current selection
      // This makes the URL shareable and maintains state on refresh
      setSearchParams({ form_id: formId });
    }
  };

  // Date filter handler
  const handleDateFilterChange = (e) => {
    const filterValue = e.target.value;
    setDateFilter(filterValue);

    // Reset custom dates when switching away from custom
    if (filterValue !== 'custom') {
      setCustomDateFrom('');
      setCustomDateTo('');
    }
  };

  // Apply date filters
  const applyDateFilter = () => {
    if (selectedId !== null) {
      fetchLeadsWithFilters();
    }
  };

  // Fetch leads with date filters
  const fetchLeadsWithFilters = () => {
    setLoader(true);
    setLeads([]);

    const requestData = {
      nonce: getNonce(),
      form_id: selectedId,
    };

    // Add date filter parameters
    if (dateFilter !== 'all') {
      requestData.date_filter = dateFilter;

      if (dateFilter === 'custom' && customDateFrom && customDateTo) {
        requestData.date_from = customDateFrom;
        requestData.date_to = customDateTo;
      }
    }

    wp.ajax.send('simpleform_get_leads', {
      data: requestData,
      success(response) {
        setLeads(response.tables || []);
        setLoader(false);
      },
      error(error) {
        console.error('Error:', error);
        setLoader(false);
      },
    });
  };

  // Data loading
  useEffect(() => {
    // Get all forms
    setLoader(true);
    wp.ajax.send('simpleform_get_tables', {
      data: {
        nonce: getNonce(),
      },
      success(response) {
        const tableData = response.tables;
        setTables(tableData);
        setTotalTables(response.tables_count || 0);

        // Check if there's a form_id in the URL query parameters
        const formIdFromUrl = searchParams.get('form_id');

        if (formIdFromUrl && tableData.length > 0) {
          // Check if the form ID from URL exists in the tables
          const formExists = tableData.find(table => table.id.toString() === formIdFromUrl);

          if (formExists) {
            console.log('Auto-selecting form from URL:', formIdFromUrl);
            setSelectedId(formIdFromUrl);

            // Optional: Remove the query parameter after selection to clean up URL
            // setSearchParams({});
          } else {
            console.warn('Form ID from URL not found in available forms:', formIdFromUrl);
            // Fallback to first form if URL form doesn't exist
            setSelectedId(tableData[0].id);
          }
        } else if (tableData.length > 0) {
          // No URL parameter, select first form as default
          setSelectedId(tableData[0].id);
        } else {
          setLoader(false);
        }
      },
      error(error) {
        console.error(error);
        setLoader(false);
      },
    });
  }, [searchParams]); // Add searchParams as dependency

  // Handle form selection changes and URL parameters
  useEffect(() => {
    if (selectedId !== null) {
      fetchLeadsWithFilters();
    }
  }, [selectedId]);

  // Handle URL parameter changes when component is already loaded
  useEffect(() => {
    const formIdFromUrl = searchParams.get('form_id');

    if (formIdFromUrl && tables.length > 0 && selectedId !== formIdFromUrl) {
      // Check if the form ID from URL exists in the tables
      const formExists = tables.find(table => table.id.toString() === formIdFromUrl);

      if (formExists) {
        console.log('Updating form selection from URL:', formIdFromUrl);
        setSelectedId(formIdFromUrl);
      }
    }
  }, [searchParams, tables, selectedId]);

  // Apply filters when date filter changes
  useEffect(() => {
    if (selectedId !== null && dateFilter !== 'all') {
      fetchLeadsWithFilters();
    }
  }, [dateFilter]);

  // Handle clicks outside the delete modal
  useEffect(() => {
    function handleCancelOutside(event) {
      if (
        confirmDeleteRef.current &&
        !confirmDeleteRef.current.contains(event.target)
      ) {
        handleClosePopup();
      }
    }

    document.addEventListener('mousedown', handleCancelOutside);
    return () => {
      document.removeEventListener('mousedown', handleCancelOutside);
    };
  }, []);

  // Setup columns for DataGrid
  const columns = leads.length > 0 && leads[0]?.fields
    ? Object.keys(JSON.parse(leads[0].fields)).slice(0, 3).map((field) => ({
      field,
      headerName: field.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()),
      flex: 1,
      renderCell: field.toLowerCase().includes('signature') || field.toLowerCase().includes('image') ?
        ImageCellRenderer : undefined,
    }))
    : [];

  // Prepare rows for DataGrid
  const rows = leads.map((lead) => {
    const leadFields = JSON.parse(lead.fields);
    return {
      id: lead.id,
      ...Object.fromEntries(Object.entries(leadFields).slice(0, 3)), // Keep only the first 3 columns
      actions: lead.id,
    };
  });

  // Add actions column
  const actionsColumn = {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <div className="action-buttons">
        <button
          className="view-button"
          onClick={(event) => openModal(params.value, event)}
          title="View Details"
        >
          <VisibilityIcon />
        </button>
        <button
          className="delete-button"
          onClick={(event) => handleDeleteTable(params.value, event)}
          title="Delete Lead"
        >
          <DeleteOutlineIcon />
        </button>
      </div>
    ),
  };

  columns.push(actionsColumn);

  return (
    <div className="leads-container">
      {loader ? (
        <div className="loader-container">
          <CircularProgress />
          <p className="loading-text">Loading leads...</p>
        </div>
      ) : (
        <>
          <div className="leads-header">
            <h1 className="leads-title">
              <InfoIcon className="leads-icon" />
              Form Submissions
            </h1>

            <div className="actions-container">
              {selectedLeads.length > 0 && (
                <button className="bulk-delete-btn" onClick={bulkDeleteLeads}>
                  <DeleteOutlineIcon />
                  Delete Selected ({selectedLeads.length})
                </button>
              )}
            </div>
          </div>

          <div className="leads-controls">
            <div className="form-selector">
              <label>Select Form:</label>
              <select
                value={selectedId || ''}
                onChange={handleSelectChange}
                className="form-select"
              >
                <option value="">Select a form</option>
                {tables.map((table) => (
                  <option key={table.id} value={table.id}>
                    {table.form_name}
                  </option>
                ))}
              </select>
              <div className="form-count">
                <span>📍 Total Forms: {totalTables}</span>
                {/* {searchParams.get('form_id') && (
                  <span className="auto-selected-indicator">
                    📍 Auto-selected from notification
                  </span>
                )} */}
              </div>
            </div>

            <div className="date-filter-section">
              <label>Filter by Date:</label>
              <select
                value={dateFilter}
                onChange={handleDateFilterChange}
                className="date-filter-select"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last_week">Last Week</option>
                <option value="last_month">Last Month</option>
                <option value="custom">Custom Range</option>
              </select>

              {dateFilter === 'custom' && (
                <div className="custom-date-range">
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    className="date-input"
                    placeholder="From"
                  />
                  <span className="date-separator">to</span>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    className="date-input"
                    placeholder="To"
                  />
                  <button
                    className="apply-filter-btn"
                    onClick={applyDateFilter}
                    disabled={!customDateFrom || !customDateTo}
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            <div className="export-options">
              <button className="export-btn json" onClick={() => exportLeads('json')} title="Export as JSON">
                <img src={jsonIcon} alt="JSON" />
                <span>JSON</span>
              </button>
              <button className="export-btn csv" onClick={() => exportLeads('csv')} title="Export as CSV">
                <img src={csvIcon} alt="CSV" />
                <span>CSV</span>
              </button>
              <button className="export-btn pdf" onClick={() => exportLeads('pdf')} title="Export as PDF">
                <img src={pdfIcon} alt="PDF" />
                <span>PDF</span>
              </button>
              <button className="export-btn print" onClick={() => exportLeads('print')} title="Print">
                <img src={printIcon} alt="Print" />
                <span>Print</span>
              </button>
            </div>
          </div>

          {leads.length > 0 ? (
            <div className="data-grid-wrapper">
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                checkboxSelection
                onSelectionModelChange={(newSelection) => {
                  setSelectedLeads(newSelection);
                }}
                autoHeight
                getRowHeight={() => 'auto'}
                disableSelectionOnClick
                className="leads-datagrid"
              />
            </div>
          ) : (
            <div className="empty-leads">
              <h3>No Leads Found</h3>
              <p>There are no submissions for this form yet.</p>
            </div>
          )}
        </>
      )}

      {/* Lead Details Modal */}
      {modalVisible && selectedLeadData && (
        <div className="modal-overlay">
          <div className="lead-modal">
            <div className="modal-header">
              <h2>Lead Details</h2>
              <button className="close-button" onClick={closeModal} aria-label="Close modal">
                <CloseIcon />
              </button>
            </div>

            <div className="modal-content">
              {/* Form Data Section */}
              <div className="modal-section">
                <h3 className="section-title">Form Data</h3>
                <div className="lead-data-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(JSON.parse(selectedLeadData.fields)).map(([key, value], index) => (
                        <tr key={index}>
                          <td className="field-name">{key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}</td>
                          <td className="field-value">
                            {typeof value === 'string' && value.startsWith('data:image') ? (
                              <div className="image-container">
                                <img src={value} alt="Lead Data" className="lead-detail-image" />
                              </div>
                            ) : (
                              <span>{String(value)}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Meta Data Section */}
              {selectedLeadData.meta && (
                <div className="modal-section">
                  <h3 className="section-title">Meta Data</h3>
                  <div className="lead-data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(JSON.parse(selectedLeadData.meta)).map(([key, value], index) => (
                          <tr key={index}>
                            <td className="field-name">{key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}</td>
                            <td className="field-value">{String(value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="secondary-btn" onClick={closeModal}>
                Cancel
              </button>
              <div className="action-buttons">
                <button className="export-btn" onClick={() => exportSingleLead(selectedLeadData)}>
                  <DownloadIcon /> Export
                </button>
                <button className="delete-btn" onClick={() => {
                  setDeleteID(selectedLeadData.id);
                  setModalVisible(false);
                  setDeleteModal(true);
                }}>
                  <DeleteOutlineIcon /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay">
          <div className="delete-modal" ref={confirmDeleteRef}>
            <div className="delete-modal-content">
              <img src={deleteConfirmImage} alt="Delete confirmation" className="delete-image" />
              <h2>Delete Lead</h2>
              <p>Are you sure you want to delete this lead? This action cannot be undone.</p>

              <div className="delete-modal-actions">
                <button className="cancel-btn" onClick={handleClosePopup}>
                  Cancel
                </button>
                <button className="confirm-btn" onClick={() => deleteLead(deleteId)}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <NoticeModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        onDecline={closeModal}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        declineText={modalConfig.declineText}
        position={modalConfig.position || 'center'}
        autoClose={modalConfig.type === 'toast'}
        autoCloseTime={3000}
      />
    </div>
  );
};

export default Leads;